import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { useSession } from '@/lib/auth-client';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { AppSidebar } from '@/components/app-sidebar';
import { ModeToggle } from '@/components/mode-toggle';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import {
  RiShieldKeyholeLine,
  RiKeyLine,
  RiHistoryLine,
  RiFileListLine,
  RiAddLine,
  RiMoreLine,
  RiDeleteBinLine,
  RiFileCopyLine,
  RiEyeLine,
  RiEyeOffLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiInformationLine,
  RiLoader2Line,
} from '@remixicon/react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

interface ApiKey {
  id: string;
  name: string;
  key: string;
  permissions: string[];
  createdAt: string;
  lastUsed: string | null;
  expiresAt: string | null;
}

interface AccessLog {
  id: string;
  userId: string;
  userName: string | null;
  action: string;
  resource: string;
  ipAddress: string;
  userAgent: string;
  timestamp: string;
  status: 'success' | 'failed';
}

interface AuditEntry {
  id: string;
  userId: string;
  userName: string | null;
  action: string;
  details: string;
  timestamp: string;
  category: 'database' | 'user' | 'settings' | 'security';
}

export function SecurityPage() {
  const { data: session } = useSession();

  // Loading states
  const [isLoadingKeys, setIsLoadingKeys] = useState(true);
  const [isLoadingLogs, setIsLoadingLogs] = useState(true);
  const [isLoadingAudit, setIsLoadingAudit] = useState(true);
  const [isCreatingKey, setIsCreatingKey] = useState(false);

  // Dialog states
  const [createKeyDialogOpen, setCreateKeyDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPermissions, setNewKeyPermissions] = useState<string[]>(['read']);
  const [showKey, setShowKey] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<{ id: string; name: string } | null>(null);

  // Data states
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [accessLogs, setAccessLogs] = useState<AccessLog[]>([]);
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);

  // Filter states
  const [logsTimeRange, setLogsTimeRange] = useState('7');
  const [auditCategory, setAuditCategory] = useState<string>('all');
  const [auditTimeRange, setAuditTimeRange] = useState('30');

  useEffect(() => {
    if (session?.user?.id) {
      loadApiKeys();
      loadAccessLogs();
      loadAuditTrail();
    }
  }, [session?.user?.id]);

  // Reload logs when filter changes
  useEffect(() => {
    if (session?.user?.id) {
      loadAccessLogs();
    }
  }, [logsTimeRange]);

  // Reload audit trail when filters change
  useEffect(() => {
    if (session?.user?.id) {
      loadAuditTrail();
    }
  }, [auditCategory, auditTimeRange]);

  const loadApiKeys = async () => {
    if (!session?.user?.id) return;

    try {
      setIsLoadingKeys(true);
      const data = await api.getApiKeys();
      setApiKeys(data.apiKeys);
    } catch (error: any) {
      console.error('Failed to load API keys', error);
      toast.error(error.message || 'Failed to load API keys');
    } finally {
      setIsLoadingKeys(false);
    }
  };

  const loadAccessLogs = async () => {
    if (!session?.user?.id) return;

    try {
      setIsLoadingLogs(true);
      const data = await api.getAccessLogs(
        50,
        parseInt(logsTimeRange)
      );
      setAccessLogs(data.logs);
    } catch (error: any) {
      console.error('Failed to load access logs', error);
      toast.error(error.message || 'Failed to load access logs');
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const loadAuditTrail = async () => {
    if (!session?.user?.id) return;

    try {
      setIsLoadingAudit(true);
      const params: any = {
        limit: 50,
        days: parseInt(auditTimeRange),
      };

      if (auditCategory !== 'all') {
        params.category = auditCategory;
      }

      const data = await api.getAuditTrail(params);
      setAuditEntries(data.entries);
    } catch (error: any) {
      console.error('Failed to load audit trail', error);
      toast.error(error.message || 'Failed to load audit trail');
    } finally {
      setIsLoadingAudit(false);
    }
  };

  const handleCreateApiKey = async () => {
    if (!session?.user?.id) return;

    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }

    if (newKeyPermissions.length === 0) {
      toast.error('Please select at least one permission');
      return;
    }

    try {
      setIsCreatingKey(true);

      await api.createApiKey({
        name: newKeyName.trim(),
        permissions: newKeyPermissions,
      });

      toast.success('API key created successfully');
      setCreateKeyDialogOpen(false);
      setNewKeyName('');
      setNewKeyPermissions(['read']);

      // Reload API keys
      await loadApiKeys();
    } catch (error: any) {
      console.error('Failed to create API key', error);
      toast.error(error.message || 'Failed to create API key');
    } finally {
      setIsCreatingKey(false);
    }
  };

  const openDeleteDialog = (keyId: string, keyName: string) => {
    setKeyToDelete({ id: keyId, name: keyName });
    setDeleteDialogOpen(true);
  };

  const handleDeleteApiKey = async () => {
    if (!session?.user?.id || !keyToDelete) return;

    try {
      await api.revokeApiKey(keyToDelete.id);
      toast.success(`API key "${keyToDelete.name}" has been revoked`);

      // Optimistic UI update
      setApiKeys(apiKeys.filter((k) => k.id !== keyToDelete.id));
      setDeleteDialogOpen(false);
      setKeyToDelete(null);
    } catch (error: any) {
      console.error('Failed to revoke API key', error);
      toast.error(error.message || 'Failed to revoke API key');
    }
  };

  const handleCopyApiKey = (key: string, keyName: string) => {
    navigator.clipboard.writeText(key);
    toast.success(`API key "${keyName}" copied to clipboard`);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'database':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'user':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'settings':
        return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
      case 'security':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const maskApiKey = (key: string) => {
    const parts = key.split('_');
    if (parts.length < 3) return key;
    return `${parts[0]}_${parts[1]}_${'*'.repeat(20)}`;
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b">
          <div className="flex items-center gap-2 px-3">
            <SidebarTrigger />
            <Separator orientation="vertical" className="h-6" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>Security</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <div className="ml-auto flex items-center gap-2 px-3">
            <ModeToggle />
          </div>
        </header>

        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                  <RiShieldKeyholeLine className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold">Security</h1>
                  <p className="text-muted-foreground">
                    Manage API keys, access logs, and audit trail
                  </p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="api-keys" className="space-y-6">
              <TabsList>
                <TabsTrigger value="api-keys">
                  <RiKeyLine className="h-4 w-4 mr-2" />
                  API Keys
                </TabsTrigger>
                <TabsTrigger value="access-logs">
                  <RiHistoryLine className="h-4 w-4 mr-2" />
                  Access Logs
                </TabsTrigger>
                <TabsTrigger value="audit-trail">
                  <RiFileListLine className="h-4 w-4 mr-2" />
                  Audit Trail
                </TabsTrigger>
              </TabsList>

              {/* API Keys Tab */}
              <TabsContent value="api-keys" className="space-y-4">
                <div className="flex items-start gap-4 p-4 border rounded-lg bg-amber-500/5 border-amber-500/20">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-500/10 flex-shrink-0">
                    <RiInformationLine className="w-4 h-4 text-amber-500" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium">Keep your API keys secure</p>
                    <p className="text-xs text-muted-foreground">
                      Never share your API keys in publicly accessible areas. Store them securely and rotate them regularly.
                    </p>
                  </div>
                </div>

                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>API Keys</CardTitle>
                        <CardDescription>
                          {isLoadingKeys ? (
                            'Loading...'
                          ) : (
                            `${apiKeys.length} API key${apiKeys.length !== 1 ? 's' : ''} configured`
                          )}
                        </CardDescription>
                      </div>
                      <Dialog open={createKeyDialogOpen} onOpenChange={setCreateKeyDialogOpen}>
                        <DialogTrigger asChild>
                          <Button>
                            <RiAddLine className="h-4 w-4 mr-2" />
                            Create API Key
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Create API Key</DialogTitle>
                            <DialogDescription>
                              Generate a new API key for programmatic access
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label htmlFor="keyName">Key Name</Label>
                              <Input
                                id="keyName"
                                placeholder="e.g., Production API Key"
                                value={newKeyName}
                                onChange={(e) => setNewKeyName(e.target.value)}
                                disabled={isCreatingKey}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="permissions">Permissions</Label>
                              <Select
                                value={newKeyPermissions[0] || 'read'}
                                onValueChange={(value) => setNewKeyPermissions([value])}
                                disabled={isCreatingKey}
                              >
                                <SelectTrigger id="permissions">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="read">Read Only</SelectItem>
                                  <SelectItem value="write">Read & Write</SelectItem>
                                  <SelectItem value="admin">Full Access</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <DialogFooter>
                            <Button
                              variant="outline"
                              onClick={() => setCreateKeyDialogOpen(false)}
                              disabled={isCreatingKey}
                            >
                              Cancel
                            </Button>
                            <Button onClick={handleCreateApiKey} disabled={isCreatingKey}>
                              {isCreatingKey ? (
                                <>
                                  <RiLoader2Line className="h-4 w-4 mr-2 animate-spin" />
                                  Creating...
                                </>
                              ) : (
                                'Create Key'
                              )}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoadingKeys ? (
                      <div className="flex items-center justify-center py-8">
                        <RiLoader2Line className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    ) : apiKeys.length === 0 ? (
                      <div className="text-center py-8">
                        <RiKeyLine className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                        <p className="text-sm text-muted-foreground">
                          No API keys configured yet
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Create your first API key to get started
                        </p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Key</TableHead>
                            <TableHead>Permissions</TableHead>
                            <TableHead>Last Used</TableHead>
                            <TableHead>Expires</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {apiKeys.map((key, index) => (
                            <motion.tr
                              key={key.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.05 }}
                            >
                              <TableCell className="font-medium">{key.name}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <code className="text-xs bg-muted px-2 py-1 rounded">
                                    {showKey === key.id ? key.key : maskApiKey(key.key)}
                                  </code>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => setShowKey(showKey === key.id ? null : key.id)}
                                  >
                                    {showKey === key.id ? (
                                      <RiEyeOffLine className="h-3 w-3" />
                                    ) : (
                                      <RiEyeLine className="h-3 w-3" />
                                    )}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => handleCopyApiKey(key.key, key.name)}
                                  >
                                    <RiFileCopyLine className="h-3 w-3" />
                                  </Button>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {key.permissions.map((perm) => (
                                    <Badge key={perm} variant="outline" className="text-xs">
                                      {perm}
                                    </Badge>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell>
                                {key.lastUsed
                                  ? new Date(key.lastUsed).toLocaleDateString()
                                  : 'Never'}
                              </TableCell>
                              <TableCell>
                                {key.expiresAt ? (
                                  <Badge variant="outline">
                                    {new Date(key.expiresAt).toLocaleDateString()}
                                  </Badge>
                                ) : (
                                  <span className="text-sm text-muted-foreground">Never</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon">
                                      <RiMoreLine className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleCopyApiKey(key.key, key.name)}>
                                      <RiFileCopyLine className="h-4 w-4 mr-2" />
                                      Copy Key
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-destructive"
                                      onClick={() => openDeleteDialog(key.id, key.name)}
                                    >
                                      <RiDeleteBinLine className="h-4 w-4 mr-2" />
                                      Revoke Key
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </motion.tr>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Access Logs Tab */}
              <TabsContent value="access-logs" className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Access Logs</CardTitle>
                        <CardDescription>
                          {isLoadingLogs ? 'Loading...' : `Recent access attempts and actions`}
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label htmlFor="logsTimeRange" className="text-sm text-muted-foreground">
                          Last
                        </Label>
                        <Select value={logsTimeRange} onValueChange={setLogsTimeRange}>
                          <SelectTrigger id="logsTimeRange" className="w-[120px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1 day</SelectItem>
                            <SelectItem value="7">7 days</SelectItem>
                            <SelectItem value="30">30 days</SelectItem>
                            <SelectItem value="90">90 days</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoadingLogs ? (
                      <div className="flex items-center justify-center py-8">
                        <RiLoader2Line className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    ) : accessLogs.length === 0 ? (
                      <div className="text-center py-8">
                        <RiHistoryLine className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                        <p className="text-sm text-muted-foreground">
                          No access logs found for the selected time range
                        </p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>User</TableHead>
                            <TableHead>Action</TableHead>
                            <TableHead>Resource</TableHead>
                            <TableHead>IP Address</TableHead>
                            <TableHead>Timestamp</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {accessLogs.map((log, index) => (
                            <motion.tr
                              key={log.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.05 }}
                            >
                              <TableCell className="font-medium">
                                {log.userName || 'Unknown User'}
                              </TableCell>
                              <TableCell>{log.action}</TableCell>
                              <TableCell>{log.resource}</TableCell>
                              <TableCell>
                                <code className="text-xs bg-muted px-2 py-1 rounded">
                                  {log.ipAddress}
                                </code>
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {formatTimestamp(log.timestamp)}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant="outline"
                                  className={
                                    log.status === 'success'
                                      ? 'bg-green-500/10 text-green-500 border-green-500/20'
                                      : 'bg-red-500/10 text-red-500 border-red-500/20'
                                  }
                                >
                                  {log.status === 'success' ? (
                                    <RiCheckLine className="h-3 w-3 mr-1" />
                                  ) : (
                                    <RiErrorWarningLine className="h-3 w-3 mr-1" />
                                  )}
                                  {log.status}
                                </Badge>
                              </TableCell>
                            </motion.tr>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Audit Trail Tab */}
              <TabsContent value="audit-trail" className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Audit Trail</CardTitle>
                        <CardDescription>
                          {isLoadingAudit
                            ? 'Loading...'
                            : 'Complete history of important actions and changes'}
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select value={auditCategory} onValueChange={setAuditCategory}>
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Categories</SelectItem>
                            <SelectItem value="database">Database</SelectItem>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="settings">Settings</SelectItem>
                            <SelectItem value="security">Security</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={auditTimeRange} onValueChange={setAuditTimeRange}>
                          <SelectTrigger className="w-[120px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="7">7 days</SelectItem>
                            <SelectItem value="30">30 days</SelectItem>
                            <SelectItem value="90">90 days</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoadingAudit ? (
                      <div className="flex items-center justify-center py-8">
                        <RiLoader2Line className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    ) : auditEntries.length === 0 ? (
                      <div className="text-center py-8">
                        <RiFileListLine className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                        <p className="text-sm text-muted-foreground">
                          No audit entries found for the selected filters
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {auditEntries.map((entry, index) => (
                          <motion.div
                            key={entry.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="flex items-start gap-4 p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                          >
                            <div className="flex-shrink-0">
                              <Badge
                                variant="outline"
                                className={getCategoryColor(entry.category)}
                              >
                                {entry.category}
                              </Badge>
                            </div>
                            <div className="flex-1 space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">
                                  {entry.userName || 'Unknown User'}
                                </span>
                                <span className="text-sm text-muted-foreground">
                                  {entry.action}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">{entry.details}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatTimestamp(entry.timestamp)}
                              </p>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </SidebarInset>

      {/* Delete API Key Alert Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API Key</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke the API key{' '}
              <span className="font-semibold text-foreground">
                "{keyToDelete?.name}"
              </span>
              ? This action cannot be undone. Any applications using this key will immediately lose access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteApiKey}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Revoke Key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  );
}
